import React, { useEffect, useState } from "react";
import {
  CreateBase,
  SimpleForm,
  TextInput,
  AutocompleteInput,
  useNotify,
  useRedirect,
  useCreate,
  useDataProvider,
  SaveButton,
  Toolbar,
} from "react-admin";
import Box from "@mui/material/Box";
import Tooltip from "@mui/material/Tooltip";
import SaveIcon from "@mui/icons-material/Save";

const CreateToolbar = () => (
  <Toolbar>
    <SaveButton
      icon={<SaveIcon />}
      sx={{
        // 让图标继承按钮文字颜色，避免主题里 MuiSvgIcon 的 #282c34 在 primary 按钮上导致图标与背景同色消失
        "& .MuiSvgIcon-root": {
          color: "inherit",
        },
      }}
    />
  </Toolbar>
);

const ActionCreate: React.FC = () => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const redirect = useRedirect();
  const [create] = useCreate();

  const [menus, setMenus] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList("permissionMenus", {
        pagination: { page: 1, perPage: 10000 },
        sort: { field: "id", order: "ASC" },
        filter: {},
      })

      .then(({ data }) => {
        const items = Array.isArray(data) ? data : [];

        // 统一 id 类型为字符串，方便查找
        const idSet = new Set(items.map((m: any) => String(m.id)));
        // 检测丢失的 parentId（便于排查后端数据问题）
        const missingParents = Array.from(
          new Set(
            items
              .map((m: any) => m.parentId)
              .filter((pid) => pid != null && !idSet.has(String(pid)))
          )
        );
        if (missingParents.length) {
          console.warn("ActionCreate: missing parentIds:", missingParents);
        }

        // 构建 parentId -> children 列表（key 用 string，顶级用 'root'）
        const childrenMap = new Map<string, any[]>();
        items.forEach((m: any) => {
          const key = m.parentId == null ? "root" : String(m.parentId);
          if (!childrenMap.has(key)) childrenMap.set(key, []);
          childrenMap.get(key)!.push(m);
        });

        // 可选：按 title 排序每个 children 列表
        const sortFn = (a: any, b: any) => (String(a.title || "").localeCompare(String(b.title || "")));
        childrenMap.forEach((arr) => arr.sort(sortFn));

        // 深度优先扁平化树，记录 depth
        const ordered: any[] = [];
        const dfs = (parentKey: string, depth: number) => {
          const children = childrenMap.get(parentKey) || [];
          children.forEach((child: any) => {
            ordered.push({ ...child, depth });
            dfs(String(child.id), depth + 1);
          });
        };
        dfs("root", 0);

        const indent = (depth: number) => "\u00A0\u00A0".repeat(Math.max(0, depth));
        const normalized = ordered.map((m: any) => ({
          ...m,
          displayTitle: `${indent(m.depth)}${m.title ?? m.name ?? ""}`,
        }));

        // setMenus([{ id: null, title: "无菜单", displayTitle: "无菜单", depth: 0 }, ...normalized]);
        setMenus(normalized);
      })
      // .catch(() => {
      //   setMenus([{ id: null, title: "无菜单" }]);
      // })
      .finally(() => setLoading(false));
  }, [dataProvider]);

  if (loading) return <span>Loading...</span>;

  const handleSave = async (data: any) => {
    try {
      await create("permissionActions", { data });
      notify("Action Created", { type: "info" });
      redirect("list", "permissionActions");
    } catch (error) {
      notify("Create Failed", { type: "error" });
    }
  };

  return (
    <CreateBase resource="permissionActions">
      <SimpleForm
        sx={{ backgroundColor: "#ffffff", p: 3 }}
        onSubmit={handleSave}
        toolbar={<CreateToolbar />}
      >
        <Box display="flex" flexWrap="wrap" gap={2}>

          {/* Action Name */}
          <Box flex="1 1 48%">
            <TextInput
              source="actionName"
              label="Action Name"
              required
              fullWidth
            />
          </Box>

          {/* Action Code */}
          <Box flex="1 1 48%">
            <Tooltip title="Action Code (create / edit / delete / overwrite / call / download / upload / export)" arrow>
              <div>
                <TextInput
                  source="actionCode"
                  label="Action Code (create / edit / delete / overwrite / call / download / upload / export)"
                  required
                  fullWidth
                />
              </div>
            </Tooltip>
          </Box>

          {/* Menu 选择 */}
          <Box flex="1 1 48%">
            <AutocompleteInput
              source="menuId"
              label="Menu"
              choices={menus}
              //展示菜单的上下级关系
              optionText={(choice: any) => choice?.displayTitle ?? choice?.title ?? '—'}
              optionValue="id"
              fullWidth
              sx={{
                flex: '1 1 48%',
                '& .MuiAutocomplete-inputRoot': { minHeight: '50px', height: '50px' },
                '& .MuiOutlinedInput-input': { height: '50px', padding: '0 12px', boxSizing: 'border-box' },
                '& .MuiAutocomplete-input': { height: '50px' },
              }}
            />
          </Box>

        </Box>
      </SimpleForm>
    </CreateBase>
  );
};

export default ActionCreate;
